import { useAuth } from '@teko/auth-react';
import { DiscoveryProduct, SearchProductPagination } from '@teko/discovery';
import {
  useDiscoverySearchV2,
  useInfiniteDiscoverySearchV2,
} from '@teko/discovery/v2';
import { ProductCardProps } from '@teko/types/dist/ecommerce';
import { WishlistButton } from 'components/WishlistButton';
import {
  useBuildPCV2,
  useCartV2,
  useFrame,
  useServices,
  useTenantConfig,
} from 'contexts';
import { createHandleEventAndCtaButton } from 'helpers';
import { useHandlerUnderEighteenYearOld } from 'hooks/useHandlerUnderEighteenYearOld';
import { useRouter } from 'next/router';
import { useMemo } from 'react';
import { ProductCarouselSlotProps } from '../slots/ProductCarousel';

export type UsePbProductReturns<T = DiscoveryProduct> = {
  products: T[];
  pagination?: SearchProductPagination;
};

export const useProductsWrapper = (
  slug: string,
  fallbackData: ProductCarouselSlotProps['serverData'],
  isEnableHamperPage?: boolean,
  pageSize: number = 20,
) => {
  const {
    isDisplayUom,
    contactLink,
    isAllowATCFromListing,
    showNewProductGift,
    epicOmni1524,
    epicOmni1821,
  } = useTenantConfig();
  const { discoveryServiceV2, discoveryHelper } = useServices();
  const { frames } = useFrame();
  const cartV2 = useCartV2();
  const buildPCV2 = useBuildPCV2();
  const { isMobile } = useTenantConfig();
  const { transformProductWhenUserUnder18 } = useHandlerUnderEighteenYearOld();
  const { loading: authLoading } = useAuth();
  const router = useRouter();

  const isEnableNewBuildPCPage = useMemo(() => {
    return router.pathname.includes('/buildpc') && !!epicOmni1524;
  }, [router.pathname, epicOmni1524]);

  const discoveryCleanUpConfig = {
    showUom: isDisplayUom,
    contactLink,
    showCtaButton: isAllowATCFromListing,
    cartV2,
    buildPCV2,
    frames,
    showNewProductGift,
    showCtaButtons:
      (isAllowATCFromListing && isEnableNewBuildPCPage) || epicOmni1821,
    showCtaButtonsWithQuantity: isEnableNewBuildPCPage,
    showCtaButtonsRelevantProducts: epicOmni1821,
    isMobile,
  };

  const { result, ...rest } = useDiscoverySearchV2(
    discoveryServiceV2,
    {
      slug: slug,
      pageSize: pageSize,
      isReturnProductsBOMSkus: isEnableNewBuildPCPage || !!isEnableHamperPage,
    },
    {
      fallbackData: fallbackData,
      transform: (data) => ({
        products: data.products?.map((product) =>
          discoveryHelper.cleanupDiscoveryV2SearchProduct(product, {
            ...discoveryCleanUpConfig,
          }),
        ),
      }),
    },
  );

  const productList = useMemo(() => {
    return (result?.products || [])
      .map((p) =>
        createHandleEventAndCtaButton(p, {
          ...discoveryCleanUpConfig,
          wishlistButton: { children: <WishlistButton sku={p.sku} /> },
        }),
      )
      .map(transformProductWhenUserUnder18);
  }, [
    JSON.stringify(result),
    JSON.stringify(cartV2.flattenItems),
    authLoading,
  ]);

  return {
    products: productList,
    ...rest,
  };
};
export const useProductsWrapperInfinite = (
  slug: string,
  config: {
    limit?: number;
    initialValue?: UsePbProductReturns<ProductCardProps>;
  },
) => {
  const {
    contactLink,
    isDisplayUom,
    isAllowATCFromListing,
    showNewProductGift,
    epicOmni1524,
    epicOmni1821,
  } = useTenantConfig();
  const { frames } = useFrame();
  const cartV2 = useCartV2();
  const buildPCV2 = useBuildPCV2();
  const { isMobile } = useTenantConfig();
  const { discoveryServiceV2, discoveryHelper } = useServices();
  const { transformProductWhenUserUnder18 } = useHandlerUnderEighteenYearOld();
  const { loading: authLoading } = useAuth();
  const router = useRouter();

  const isEnableNewBuildPCPage = useMemo(() => {
    return router.pathname.includes('/buildpc') && !!epicOmni1524;
  }, [router.pathname, epicOmni1524]);

  const discoveryCleanUpConfig = {
    showUom: isDisplayUom,
    contactLink,
    showCtaButton: isAllowATCFromListing,
    cartV2,
    buildPCV2,
    frames,
    showNewProductGift,
    showCtaButtons:
      (isAllowATCFromListing && isEnableNewBuildPCPage) || epicOmni1821,
    showCtaButtonsWithQuantity: isEnableNewBuildPCPage,
    showCtaButtonsRelevantProducts: !!epicOmni1821,
    isMobile,
  };

  const { data, loading, loadmore, hasMore, error, isEmpty, size } =
    useInfiniteDiscoverySearchV2(
      discoveryServiceV2,
      {
        slug: slug,
        pageSize: config.limit,
        isReturnProductsBOMSkus: isEnableNewBuildPCPage,
      },
      {
        transform: (data) => ({
          products: data.products?.map((product) =>
            discoveryHelper.cleanupDiscoveryV2SearchProduct(product, {
              ...discoveryCleanUpConfig,
            }),
          ),
          total: data.total,
        }),
        fallbackData: [
          {
            products: config.initialValue?.products,
            total: config.initialValue?.pagination?.totalItems || 0,
          },
        ],
      },
    );

  const products = data?.reduce(
    (pageData, d) => [...pageData, ...(d.products || [])],
    [] as ProductCardProps[],
  );

  const productList = useMemo(() => {
    return (products || [])
      .map((p) =>
        createHandleEventAndCtaButton(p, {
          ...discoveryCleanUpConfig,
          wishlistButton: { children: <WishlistButton sku={p.sku} /> },
        }),
      )
      .map(transformProductWhenUserUnder18);
  }, [
    JSON.stringify(products),
    JSON.stringify(cartV2.flattenItems),
    authLoading,
  ]);

  return [
    {
      products: productList,
      loading,
      hasMore,
      error,
      size,
      isEmpty,
    },
    { onNextPage: loadmore },
  ] as const;
};
